/**
 * Helper: GraphQL
 * -----------------------------------------------------------------------------
 * Handles GraphQL responses.
 * - Add in alphabetical order.
 *
 */

/**
 * Removes edges and nodes from GraphQL data.
 * @param {Object} object - GraphQL data.
 * @param {String} field - Optional string to filter by.
 * @returns {Object}
 */
export function transformEdges(object, field) {
  if (!object) {
    return [];
  }

  if (!field) {
    return object.edges.map((edge) => {
      return edge.node;
    });
  }

  return object.edges.map((edge) => {
    return edge.node[field];
  });
}

/**
 * Transform the edges, nodes and top level attributes from GraphQL data
 * to an Object (fields, references).
 * @param {Object} object - GraphQL data.
 * @param {String} field - Optional string to filter by.
 * @returns {Object}
 */
export function transformEdgesToObject(object, field) {
  if (!object) {
    return [];
  }

  return object.edges.map((edge) => {
    const node = field ? edge.node[field] : edge.node;
    return node ? transformFieldsToObject(node) : {};
  });
}

/**
 * Transform node to key-value pairs.
 * @param {Object} node - Node object.
 * @returns {Object}
 */
export function transformFieldsToObject(node) {
  const transformedObject = { ...node };

  if (node.fields) {
    node.fields.forEach(({ key, value, reference, references }) => {
      if (reference) {
        transformedObject[key] = transformReference(reference);
      } else if (references) {
        transformedObject[key] = references.nodes.map(transformFieldsToObject);
      } else {
        try {
          transformedObject[key] = JSON.parse(value);
        } catch {
          transformedObject[key] = value;
        }
      }
    });
    // Remove the fields key after processing
    delete transformedObject.fields;
  }

  return transformedObject;
}

/**
 * Transform a reference object.
 * @param {Object} reference - Reference object.
 * @returns {Object}
 */
export function transformReference(reference) {
  if (typeof reference !== 'object' || reference === null) {
    return reference;
  }

  const transformedReference = Array.isArray(reference) ? [] : {};

  for (const key in reference) {
    transformedReference[key] = transformReference(reference[key]);
  }

  return transformedReference;
}
